import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// No Paystack public key in the app (2026-10-14): subscription checkout is server-initialised
// (Paystack-hosted page) and in-app client-fee collection is disabled.

export const PLANS = {
  solo: {
    id: 'solo',
    name: 'Solo',
    icon: '📌',
    tagline: 'For solo practitioners',
    monthlyPrice: 349,
    annualPrice: 3490,
    limits: { users: 1, cases: 100, clients: 100, documents: 100, storageMB: 500 },
    features: [
      'AI Paralegal — unlimited queries',
      'Case management (up to 100)',
      'Client management (up to 100)',
      'Document management (up to 100)',
      '📄 Document Intelligence — ask questions about your files (3/day)',
      '📋 Court Forms — 10 essential templates with auto-fill',
      '⚖️ 2 Procedural Playbooks (Civil Litigation + Criminal Defence)',
      '🧠 Legal Quiz — 4 core categories',
      '📅 Court dates with auto-reminders (WhatsApp)',
      'Billing & time tracking',
      'WhatsApp notifications',
      'Client portal',
      'Legal research',
      '500MB file storage',
    ],
    notIncluded: ['Email notifications', 'Precedent-Aware Drafting', 'Contract Review & Playbooks', 'Agent user role', 'Firm Automations', 'Templates library', 'Export CSV', 'Analytics dashboard', 'Team hub', 'Google Calendar sync', 'Custom branding', 'All procedural playbooks (8 more)', 'All quiz categories (9 more)'],
    featureAccess: { analytics: false, teamHub: false, calendarSync: false, letterhead: false, exportCSV: false, printReport: false, templates: false, uploadTemplates: false, emailNotifications: false, agentRole: false, aiAutomation: false, docIntelligence: true, docIntelligenceDaily: 3, precedentDrafting: false, courtForms: true, courtFormsLimit: 10, contractReview: false, customPlaybooks: false, batchProcessing: false, playbookLimit: 2, courtReminders: true, quizCategoryLimit: 4, quizLeaderboard: false },
  },
  firm: {
    id: 'firm',
    name: 'Firm',
    icon: '⚖️',
    tagline: 'For small to mid-size firms',
    monthlyPrice: 649,
    annualPrice: 6490,
    popular: true,
    limits: { users: 10, cases: 300, clients: 300, documents: 300, storageMB: 5120 },
    features: [
      'Everything in Solo, plus:',
      'Up to 10 team members',
      'Up to 300 cases, clients & documents',
      '📄 Unlimited Document Intelligence + comparison',
      '✍️ Precedent-Aware Drafting — uses your past work (5/day)',
      '🔍 Contract Review with preset playbooks (3/day)',
      '📋 All 143 court form templates with auto-fill',
      '⚖️ All 19 procedural playbooks with deadline tracking',
      '🧠 Full legal quiz — all 13 categories (297 questions)',
      '📅 Court date auto-reminders (48h / 24h / 2h via WhatsApp)',
      '📊 Print & export reports',
      'Templates library',
      'Analytics & reports dashboard',
      'Team hub & role management',
      'Google Calendar sync',
      'Client portal with branding',
      'Agent user role',
      '5GB file storage',
    ],
    notIncluded: ['Firm Automations (payment follow-ups, matter inactivity monitoring)', 'Export CSV (raw data)', 'Custom branding / letterhead', 'Custom playbooks', 'Quiz team leaderboard', 'Priority support', 'API access'],
    featureAccess: { analytics: true, teamHub: true, calendarSync: true, letterhead: false, exportCSV: false, printReport: true, templates: true, uploadTemplates: false, emailNotifications: true, agentRole: true, aiAutomation: false, docIntelligence: true, docIntelligenceDaily: Infinity, precedentDrafting: true, precedentDraftingDaily: 5, courtForms: true, courtFormsLimit: Infinity, contractReview: true, contractReviewDaily: 3, customPlaybooks: false, batchProcessing: false, playbookLimit: Infinity, courtReminders: true, quizCategoryLimit: Infinity, quizLeaderboard: false },
  },
  enterprise: {
    id: 'enterprise',
    name: 'Enterprise',
    icon: '🏛️',
    tagline: 'For large firms & legal departments',
    monthlyPrice: 999,
    annualPrice: 9990,
    limits: { users: Infinity, cases: Infinity, clients: Infinity, documents: Infinity, storageMB: Infinity },
    features: [
      'Everything in Firm, plus:',
      'Unlimited team members',
      'Unlimited cases, clients & documents',
      '📄 Batch document processing',
      '✍️ Unlimited precedent-aware drafting + cross-case learning',
      '🔍 Unlimited contract review + custom playbooks',
      '📋 All court forms + custom templates + bulk generation',
      '🧠 Quiz team leaderboard — track associate performance',
      'Firm Automations — payment follow-ups, matter inactivity monitoring, management briefing, case status client update drafts',
      'Export CSV (raw data for external analysis)',
      'Custom firm branding & letterhead',
      'Unlimited storage',
      'Priority email & WhatsApp support',
      'API access',
      'Dedicated onboarding session',
      'Custom integrations',
    ],
    notIncluded: [],
    featureAccess: { analytics: true, teamHub: true, calendarSync: true, letterhead: true, exportCSV: true, printReport: true, templates: true, uploadTemplates: true, emailNotifications: true, agentRole: true, aiAutomation: true, docIntelligence: true, docIntelligenceDaily: Infinity, precedentDrafting: true, precedentDraftingDaily: Infinity, courtForms: true, courtFormsLimit: Infinity, contractReview: true, contractReviewDaily: Infinity, customPlaybooks: true, batchProcessing: true, playbookLimit: Infinity, courtReminders: true, quizCategoryLimit: Infinity, quizLeaderboard: true },
  },
};

/**
 * The firm's own server-set trial end as "30 January 2027", always in
 * Africa/Accra time (UTC) so 23:59:59.999Z never renders as the next day.
 * Falls back to the original cohort date for a firm with no recorded end.
 */
export const formatTrialEnd = (trialEnds) => {
  const te = trialEnds?.toDate?.() || (trialEnds?.seconds ? new Date(trialEnds.seconds * 1000) : null) || new Date(trialEnds || '2026-12-31T23:59:59.999Z');
  const d = Number.isNaN(te.getTime()) ? new Date('2026-12-31T23:59:59.999Z') : te;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Accra' });
};

// Phase 2 (2026-10-12) — mirrors functions/firmEntitlementService.js
// resolveEffectiveEntitlement. The SERVER is authoritative (every premium
// callable enforces it); this only shapes the UI. Boundaries are exclusive.
const COHORT_TRIAL_END = '2026-12-31T23:59:59.999Z';
export const PAID_GRACE_DAYS = 3;
const DAY_MS = 1000 * 60 * 60 * 24;
const toDateValue = (v, fallback) => {
  try {
    const d = v?.toDate?.() || (v?.seconds ? new Date(v.seconds * 1000) : null) || (v instanceof Date ? v : null) || ((typeof v === 'string' || typeof v === 'number') ? new Date(v) : null);
    if (d && !Number.isNaN(d.getTime())) return d;
  } catch (e) { /* fall through */ }
  return fallback ? new Date(fallback) : null;
};
const isGenuinePaid = (sub) => !!sub && !sub.testMode && !sub.simulated;

/**
 * Check subscription status for a firm.
 * @returns {{ active, plan, daysLeft, isTrialing, isExpired, isPendingSelection?, isInGrace?, graceEndsAt?, isSuperAdmin? }}
 */
export const checkSubscription = (firmProfile, userProfile) => {
  if (userProfile?.platformAdmin === true) { // derived from the superAdmin token claim (App.js), never a stored profile field
    return { active: true, plan: 'enterprise', daysLeft: 9999, isTrialing: false, isExpired: false, isSuperAdmin: true };
  }

  if (!firmProfile) return { active: false, plan: null, daysLeft: 0, isTrialing: false, isExpired: true };

  const sub = firmProfile.subscription;
  const now = Date.now();
  const daysUntil = (d) => Math.max(0, Math.ceil((d.getTime() - now) / DAY_MS));

  if (!sub || typeof sub === 'string') {
    const end = new Date(COHORT_TRIAL_END);
    const trialActive = now < end.getTime();
    return { active: trialActive, plan: 'trial', daysLeft: daysUntil(end), isTrialing: trialActive, isExpired: !trialActive, trialEnds: end };
  }

  if (sub.status === 'pending_selection') {
    const end = toDateValue(sub.trialEnds, COHORT_TRIAL_END);
    if (now >= end.getTime()) return { active: false, plan: null, daysLeft: 0, isTrialing: false, isExpired: true, trialEnds: end };
    return { active: true, plan: null, daysLeft: daysUntil(end), isTrialing: false, isExpired: false, isPendingSelection: true, trialEnds: end };
  }

  if (sub.status === 'trialing') {
    const end = toDateValue(sub.trialEnds, COHORT_TRIAL_END);
    const trialActive = now < end.getTime();
    return { active: trialActive, plan: sub.plan || 'solo', daysLeft: daysUntil(end), isTrialing: trialActive, isExpired: !trialActive, trialEnds: end };
  }

  const expiresAt = toDateValue(sub.expiresAt, 0);
  const paidStatus = sub.status === 'active' || sub.status === 'cancelled';
  const withinTerm = paidStatus && expiresAt.getTime() > now;
  const graceEnds = new Date(expiresAt.getTime() + PAID_GRACE_DAYS * DAY_MS);
  const inGrace = paidStatus && !withinTerm && isGenuinePaid(sub) && now < graceEnds.getTime();
  const isActive = withinTerm || inGrace;

  return {
    active: isActive,
    plan: sub.plan || 'solo',
    daysLeft: daysUntil(expiresAt),
    isTrialing: false,
    isExpired: !isActive,
    isInGrace: inGrace,
    graceEndsAt: inGrace ? graceEnds : null,
    expiresAt,
    billing: sub.billing || 'monthly',
    // Paystack binding fields are server-only (billingPrivate) and never read by the app.
  };
};

// Approved expired-firm policy: essential practice management stays; new
// paid computational services (AI, research, document intelligence, premium
// automations, premium template generation) stop; exports and printing of
// the firm's own records stay available.
const EXPIRED_OVERRIDES = {
  aiAssistant: false, aiResearch: false, aiAutomation: false,
  docIntelligence: false, docIntelligenceDaily: 0, precedentDrafting: false, precedentDraftingDaily: 0,
  contractReview: false, contractReviewDaily: 0, customPlaybooks: false, batchProcessing: false,
  templates: false, uploadTemplates: false, courtForms: false, courtFormsLimit: 0,
  exportCSV: true, printReport: true, courtReminders: true, subscriptionExpired: true,
};
const NO_PLAN_FEATURES = { analytics: true, teamHub: true, calendarSync: true, letterhead: false, exportCSV: false, printReport: true, templates: true, uploadTemplates: false, emailNotifications: true, agentRole: true, aiAutomation: false, docIntelligence: true, docIntelligenceDaily: Infinity, precedentDrafting: true, precedentDraftingDaily: 5, courtForms: true, courtFormsLimit: Infinity, contractReview: true, contractReviewDaily: 3, customPlaybooks: false, batchProcessing: false, playbookLimit: Infinity, courtReminders: true, quizCategoryLimit: Infinity, quizLeaderboard: false };

/**
 * Get feature access flags for the current plan.
 */
export const getPlanFeatures = (firmProfile, userProfile) => {
  if (userProfile?.platformAdmin === true) { // derived from the superAdmin token claim (App.js), never a stored profile field
    return { analytics: true, teamHub: true, calendarSync: true, letterhead: true, exportCSV: true, printReport: true, templates: true, uploadTemplates: true, emailNotifications: true, agentRole: true, aiAutomation: true, docIntelligence: true, docIntelligenceDaily: Infinity, precedentDrafting: true, precedentDraftingDaily: Infinity, courtForms: true, courtFormsLimit: Infinity, contractReview: true, contractReviewDaily: Infinity, customPlaybooks: true, batchProcessing: true, playbookLimit: Infinity, courtReminders: true, quizCategoryLimit: Infinity, quizLeaderboard: true, aiAssistant: true, aiResearch: true };
  }

  const sub = firmProfile?.subscription;
  const base = (!sub || !sub.plan) ? NO_PLAN_FEATURES : (PLANS[sub.plan]?.featureAccess || PLANS.solo.featureAccess);
  if (firmProfile && checkSubscription(firmProfile, userProfile).isExpired) return { ...base, ...EXPIRED_OVERRIDES };
  return { ...base, aiAssistant: true, aiResearch: true };
};

/**
 * Redirect to Paystack hosted checkout to start a subscription.
 */
export const initPaystack = async ({ plan, billing, firmId, onError }) => {
  try {
    const initFn = httpsCallable(functions, 'initializeSubscription');
    const callbackUrl = window.location.origin + window.location.pathname;
    const result = await initFn({ plan, billing, firmId, callbackUrl });

    if (!result.data.success) {
      if (onError) onError('Failed to initialize payment');
      return;
    }

    const { authorizationUrl, reference } = result.data;
    sessionStorage.setItem('paystackPending', JSON.stringify({ reference, plan, billing, firmId }));
    window.location.href = authorizationUrl;
  } catch (e) {
    console.error('[subscription] initPaystack failed:', e);
    if (onError) onError(e.message);
  }
};

/**
 * Check plan limits before creating a resource.
 * @param {string} firmId
 * @param {string} resource - e.g. 'cases', 'clients', 'documents'
 * @param {function} showToast - toast(message, type) to surface errors to the user
 * @returns {boolean} true if allowed, false if limit exceeded
 */
export const checkLimit = async (firmId, resource, showToast) => {
  try {
    const fn = httpsCallable(functions, 'checkPlanLimits');
    const result = await fn({ firmId, resource });
    if (!result.data.allowed) {
      if (showToast) showToast(`Plan limit reached: ${result.data.current}/${result.data.limit} ${result.data.resource}. Upgrade your plan for more.`, 'error');
      return false;
    }
    return true;
  } catch (e) {
    console.warn('[subscription] checkLimit failed:', e.message);
    if (showToast) showToast('Could not verify plan limits. Please try again.', 'error');
    return false;
  }
};
